import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import { Nav } from "./components/nav.tsx";
import { Hero } from "./components/hero.tsx";
import { GameCards } from "./components/game-cards.tsx";
import { EmailSignup } from "./components/email-signup.tsx";
import { AuthForm } from "./components/auth-form.tsx";
import { Leaderboard } from "./components/leaderboard.tsx";
import { Dashboard } from "./components/dashboard.tsx";
import { GameChrome } from "./components/game-chrome.tsx";
import { DailyFiveProgress } from "./components/daily-five-progress.tsx";
import type { User, GameName, Completion, DifficultyLevel } from "./types.ts";
import { ALL_GAMES, ALL_DIFFICULTIES, DEFAULT_DIFFICULTY } from "./types.ts";
import { getLocalCompletedGames } from "./lib/local-completions.ts";

// Game apps
import { App as FoldawayApp } from "../foldaway/src/app.tsx";
import { App as SpilloverApp } from "../spillover/src/app.tsx";
import { App as LockstepApp } from "../lockstep/src/app.tsx";
import { App as BurnoutApp } from "../burnout/src/app.tsx";
import { App as EchoApp } from "../echo/src/app.tsx";

// ── Hash Routing ──

const VALID_VIEWS = new Set(["home", "leaderboard", "dashboard", "foldaway", "spillover", "lockstep", "burnout", "echo"]);

function parseHash(): string {
  const hash = window.location.hash;
  if (hash.startsWith("#auth-callback")) return "home";
  const view = hash.replace("#/", "").replace("#", "") || "home";
  return VALID_VIEWS.has(view) ? view : "home";
}

function setHash(view: string) {
  const target = view === "home" ? "/" : `#/${view}`;
  if (window.location.hash !== `#/${view}` && !(view === "home" && !window.location.hash)) {
    window.history.pushState(null, "", view === "home" ? window.location.pathname : `#/${view}`);
  }
}

function useHashRoute() {
  const [view, setViewState] = useState(parseHash);

  useEffect(() => {
    const onHash = () => setViewState(parseHash());
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, []);

  const navigate = useCallback((v: string) => {
    setHash(v);
    setViewState(v);
    window.scrollTo(0, 0);
  }, []);

  return { view, navigate };
}

// ── Auth Context ──

interface AuthState {
  user: User | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

function useAuthProvider(): AuthState {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("neurobics-token"));
  const [user, setUser] = useState<User | null>(null);

  const fetchMe = useCallback(async (t: string) => {
    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        localStorage.removeItem("neurobics-token");
        setToken(null);
        setUser(null);
      }
    } catch {
      // network error, keep token for retry
    }
  }, []);

  useEffect(() => {
    if (token) fetchMe(token);
  }, [token, fetchMe]);

  // Handle OAuth callback token from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#auth-callback")) {
      const params = new URLSearchParams(hash.slice(hash.indexOf("?") + 1));
      const t = params.get("token");
      if (t) {
        localStorage.setItem("neurobics-token", t);
        setToken(t);
        window.location.hash = "";
      }
    }
  }, []);

  const login = useCallback((t: string) => {
    localStorage.setItem("neurobics-token", t);
    setToken(t);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("neurobics-token");
    setToken(null);
    setUser(null);
  }, []);

  return { user, token, login, logout };
}

// ── Today's Completions Context ──

interface TodayState {
  completions: Completion[];
  completedGames: Set<GameName>;
  refresh: () => void;
}

const TodayContext = createContext<TodayState>({ completions: [], completedGames: new Set(), refresh: () => {} });
export const useToday = () => useContext(TodayContext);

function useTodayProvider(token: string | null): TodayState {
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [localCompleted, setLocalCompleted] = useState<Set<GameName>>(() => getLocalCompletedGames());

  const refresh = useCallback(async () => {
    // Always refresh localStorage completions
    setLocalCompleted(getLocalCompletedGames());

    if (!token) return;
    try {
      const res = await fetch("/api/completions/today", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCompletions(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge server completions + localStorage completions
  const completedGames = useMemo(() => {
    const merged = new Set<GameName>(localCompleted);
    for (const c of completions) {
      merged.add(c.game);
    }
    return merged;
  }, [completions, localCompleted]);

  return { completions, completedGames, refresh };
}

// ── Difficulty Context ──

interface DifficultyState {
  difficulty: DifficultyLevel;
  setDifficulty: (d: DifficultyLevel) => void;
}

const DifficultyContext = createContext<DifficultyState>({
  difficulty: DEFAULT_DIFFICULTY,
  setDifficulty: () => {},
});

export const useDifficulty = () => useContext(DifficultyContext);

function useDifficultyProvider(): DifficultyState {
  const [difficulty, setDifficultyRaw] = useState<DifficultyLevel>(() => {
    const stored = localStorage.getItem("neurobics-difficulty");
    if (stored && ALL_DIFFICULTIES.includes(stored as DifficultyLevel)) {
      return stored as DifficultyLevel;
    }
    return DEFAULT_DIFFICULTY;
  });

  const setDifficulty = useCallback((d: DifficultyLevel) => {
    localStorage.setItem("neurobics-difficulty", d);
    setDifficultyRaw(d);
  }, []);

  return { difficulty, setDifficulty };
}

// ── Deterministic daily shuffle for game order ──

function dailyShuffledGames(completedGames: Set<GameName>): GameName[] {
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const unsolved = ALL_GAMES.filter(g => !completedGames.has(g));
  // Simple Fisher-Yates with deterministic seed
  let s = seed;
  const next = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 0x100000000; };
  const arr = [...unsolved];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

// ── Game Views ──

const GAME_VIEWS: Record<string, { component: typeof FoldawayApp; game: GameName; color: string; label: string; emoji: string }> = {
  foldaway: { component: FoldawayApp, game: "foldaway", color: "#A78BFA", label: "Foldaway", emoji: "\uD83D\uDCD0" },
  spillover: { component: SpilloverApp, game: "spillover", color: "#4ECAFF", label: "Spillover", emoji: "\uD83D\uDCA7" },
  lockstep: { component: LockstepApp, game: "lockstep", color: "#FFD166", label: "Lockstep", emoji: "\u2699\uFE0F" },
  burnout: { component: BurnoutApp, game: "burnout", color: "#FF6B6B", label: "Burnout", emoji: "\uD83D\uDD25" },
  echo: { component: EchoApp, game: "echo", color: "#00F0A0", label: "Echo", emoji: "\uD83D\uDCE1" },
};

function GameWrapper({ gameKey, token, onNavigate }: { gameKey: string; token: string | null; onNavigate: (v: string) => void }) {
  const gameView = GAME_VIEWS[gameKey];
  const { refresh, completedGames } = useToday();
  const { difficulty } = useDifficulty();
  if (!gameView) return null;

  const { component: GameComponent, game, color, label, emoji } = gameView;

  const handleWin = useCallback(async (data: { stars: number; metric: number; timeMs: number }) => {
    if (!token) return;
    try {
      await fetch("/api/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          game,
          stars: data.stars,
          metric: data.metric,
          timeMs: data.timeMs,
        }),
      });
      refresh();
    } catch {}
  }, [token, game, refresh]);

  // Pick next random unsolved game (deterministic daily order)
  const shuffled = dailyShuffledGames(new Set([...completedGames, game]));
  const nextGame = shuffled[0] ?? null;
  const doneCount = completedGames.size;

  // Extra content injected into each game's win screen
  const winExtra = (
    <div className="space-y-3 pt-2">
      {/* Daily Five mini-progress */}
      <div className="flex items-center justify-center gap-2">
        <span className="font-mono text-[10px] tracking-[1px] text-slate-500">{Math.min(doneCount + 1, 5)}/5</span>
        {ALL_GAMES.map(g => {
          const done = completedGames.has(g) || g === game;
          const gv = GAME_VIEWS[g];
          return (
            <div
              key={g}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ background: done ? gv!.color : "rgba(255,255,255,0.1)" }}
            />
          );
        })}
      </div>
      {/* Next game button */}
      {nextGame ? (
        <button
          onClick={() => onNavigate(nextGame)}
          className="px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
          style={{ background: GAME_VIEWS[nextGame]!.color, color: "#0D0F1C" }}
        >
          Next: {GAME_VIEWS[nextGame]!.label} &rarr;
        </button>
      ) : doneCount + 1 >= 5 ? (
        <button
          onClick={() => onNavigate("home")}
          className="px-5 py-2 rounded-lg text-sm font-semibold bg-electric-mint text-midnight"
        >
          Daily Five Complete!
        </button>
      ) : null}
      {/* Back to games link */}
      <div>
        <button
          onClick={() => onNavigate("home")}
          className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          &larr; Back to Games
        </button>
      </div>
    </div>
  );

  return (
    <GameChrome
      gameKey={gameKey}
      label={label}
      emoji={emoji}
      color={color}
      difficulty={difficulty}
      onBack={() => onNavigate("home")}
      onNextGame={nextGame ? () => onNavigate(nextGame) : undefined}
      nextGameLabel={nextGame ? GAME_VIEWS[nextGame]?.label : undefined}
      completedGames={completedGames}
    >
      <GameComponent onWin={handleWin} embedded winExtra={winExtra} difficulty={difficulty} />
    </GameChrome>
  );
}

// ── App ──

// ── Difficulty Selector ──

const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  expert: "Expert",
};

function DifficultySelector() {
  const { difficulty, setDifficulty } = useDifficulty();
  const [confirmText, setConfirmText] = useState<string | null>(null);

  const handleChange = useCallback((d: DifficultyLevel) => {
    if (d === difficulty) return;
    setDifficulty(d);
    setConfirmText(`Difficulty set to ${DIFFICULTY_LABELS[d]} for all puzzles`);
    const timer = setTimeout(() => setConfirmText(null), 3000);
    return () => clearTimeout(timer);
  }, [difficulty, setDifficulty]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="font-mono text-[10px] tracking-[3px] uppercase text-warm-gray">Difficulty</div>
      <div className="flex gap-2">
        {ALL_DIFFICULTIES.map(d => (
          <button
            key={d}
            onClick={() => handleChange(d)}
            className={`font-display font-semibold text-xs px-4 py-1.5 rounded-full border transition-all duration-200 ${
              d === difficulty
                ? "bg-electric-mint text-midnight border-electric-mint"
                : "border-[rgba(255,255,255,0.15)] text-warm-gray hover:border-electric-mint hover:text-electric-mint"
            }`}
          >
            {DIFFICULTY_LABELS[d]}
          </button>
        ))}
      </div>
      {confirmText && (
        <p className="text-xs text-electric-mint animate-[fadeIn_0.2s_ease-out]">
          {confirmText}
        </p>
      )}
    </div>
  );
}

// ── Completion Memo ──

function CompletionMemo() {
  return (
    <section className="w-full">
      <div className="rounded-[16px] p-8 border border-[rgba(0,240,160,0.15)] text-center space-y-4"
        style={{ background: "linear-gradient(145deg, #141729, rgba(0,240,160,0.08))" }}>
        <div className="flex justify-center gap-2">
          {ALL_GAMES.map(g => (
            <div key={g} className="w-3 h-3 rounded-full bg-electric-mint" />
          ))}
        </div>
        <h2 className="font-display font-extrabold text-2xl text-electric-mint">
          Daily Five Complete!
        </h2>
        <p className="text-sm text-warm-gray max-w-md mx-auto">
          You've solved all five daily puzzles. Come back tomorrow for new ones.
        </p>
      </div>
    </section>
  );
}

// ── App ──

export function App() {
  const auth = useAuthProvider();
  const today = useTodayProvider(auth.token);
  const difficultyState = useDifficultyProvider();
  const { view, navigate } = useHashRoute();
  const [showAuth, setShowAuth] = useState(false);

  const isGame = view in GAME_VIEWS;
  const allFiveDone = today.completedGames.size >= 5;

  // "Start Your Daily Five" — go to a random unsolved game
  const handleStartDailyFive = useCallback(() => {
    const shuffled = dailyShuffledGames(today.completedGames);
    if (shuffled.length > 0) {
      navigate(shuffled[0]!);
    } else {
      // All solved — scroll to completion memo
      document.getElementById("games")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [today.completedGames, navigate]);

  return (
    <AuthContext.Provider value={auth}>
      <TodayContext.Provider value={today}>
        <DifficultyContext.Provider value={difficultyState}>
          <Nav
            currentView={view}
            onNavigate={navigate}
            user={auth.user}
            onAuthClick={() => setShowAuth(true)}
            isGame={isGame}
            gameLabel={isGame ? GAME_VIEWS[view]?.label : undefined}
            gameColor={isGame ? GAME_VIEWS[view]?.color : undefined}
            difficulty={isGame ? difficultyState.difficulty : undefined}
          />

          <div className="animate-fade-in-up" key={view}>
            {isGame ? (
              <GameWrapper gameKey={view} token={auth.token} onNavigate={navigate} />
            ) : (
              <main className="max-w-[1100px] mx-auto px-6 md:px-10">
                {view === "home" && (
                  <>
                    <Hero onCta={handleStartDailyFive} />

                    {/* Divider */}
                    <div className="h-px my-16" style={{ background: "linear-gradient(90deg, transparent, rgba(0,240,160,0.2), transparent)" }} />

                    {/* Difficulty selector */}
                    <DifficultySelector />

                    {/* Spacer */}
                    <div className="h-8" />

                    <div id="games">
                      {allFiveDone ? (
                        <CompletionMemo />
                      ) : (
                        <GameCards onPlay={navigate} />
                      )}
                    </div>

                    {/* Divider */}
                    <div className="h-px my-16" style={{ background: "linear-gradient(90deg, transparent, rgba(0,240,160,0.2), transparent)" }} />

                    <EmailSignup />

                    {/* Footer */}
                    <footer className="py-16 text-center">
                      <p className="font-mono text-[10px] tracking-[3px] uppercase text-mid-gray">
                        neurobics.io &mdash; daily brain training
                      </p>
                    </footer>
                  </>
                )}

                {view === "leaderboard" && <Leaderboard />}

                {view === "dashboard" && auth.user && auth.token && (
                  <Dashboard
                    token={auth.token}
                    displayName={auth.user.display_name}
                    onLogout={auth.logout}
                  />
                )}
              </main>
            )}
          </div>

          {isGame && (
            <DailyFiveProgress completedGames={today.completedGames} />
          )}

          {showAuth && (
            <AuthForm
              onClose={() => setShowAuth(false)}
              onLogin={auth.login}
            />
          )}
        </DifficultyContext.Provider>
      </TodayContext.Provider>
    </AuthContext.Provider>
  );
}
